import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Album, AlbumContributor } from "../../../types";
import { mediaUrl } from "../../../mediaAuth";
import { AvatarImage } from "../../components/AvatarImage";

const MAX_VISIBLE_TAGS = 2;

// Same premium gradient + folder-icon treatment either way - a missing
// cover (no photo added yet) and a broken/unreachable one (stale token,
// deleted file the album record hasn't caught up with) should look
// identical to the viewer, never a browser broken-image icon.
function CardCover({ cover }: { cover: string | null | undefined }) {
  const [failed, setFailed] = useState(false);
  if (!cover || failed) {
    return (
      <div className="albums-card-cover-placeholder">
        <i className="bi bi-folder2-open" aria-hidden="true" />
      </div>
    );
  }
  return <img src={mediaUrl(`/assets/thumbs/${cover}`)} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

function contributorAvatar(c: AlbumContributor) {
  return c.avatarFilename ? (
    <AvatarImage
      filename={c.avatarFilename}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
      fallback={c.displayName.charAt(0).toUpperCase()}
    />
  ) : (
    c.displayName.charAt(0).toUpperCase()
  );
}

// Lives in the metadata section now, not floating over the cover - as an
// overlay on the image it read as a story/profile badge rather than album
// collaboration; here it reads as "who's in this album" alongside the
// other metadata. Names spelled out next to the avatars (not just
// silhouettes) per the same reasoning. Real data only - omitted entirely
// for legacy albums with no known contributor, never a placeholder.
function ContributorsRow({ contributors, favoriteCount }: { contributors: AlbumContributor[]; favoriteCount: number }) {
  if (!contributors.length) return null;
  return (
    <div className="albums-card-contributors-row" aria-label={`Added by ${contributors.map((c) => c.displayName).join(", ")}`}>
      <span className="albums-card-contributors-who">
        <span className="albums-card-contributor-avatars">
          {contributors.map((c) => (
            <span className="albums-card-contributor-avatar" key={c.id} title={c.displayName} aria-hidden="true">
              {contributorAvatar(c)}
            </span>
          ))}
        </span>
        <span className="albums-card-contributor-names text-truncate">{contributors.map((c) => c.displayName).join(" & ")}</span>
      </span>
      {favoriteCount > 0 && (
        <span className="albums-card-favorites">
          <i className="bi bi-heart-fill" aria-hidden="true" /> {favoriteCount}
        </span>
      )}
    </div>
  );
}

function TagChips({ tags }: { tags: string[] }) {
  if (!tags.length) return null;
  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = tags.length - visible.length;
  return (
    <div className="albums-card-tags">
      {visible.map((tag) => (
        <span className="albums-tag-chip" key={tag}>
          {tag}
        </span>
      ))}
      {overflow > 0 && <span className="albums-tag-chip is-overflow">+{overflow}</span>}
    </div>
  );
}

// Featured-carousel card (AlbumsListScreen.tsx). Cover fills the upper
// portion edge-to-edge, metadata sits below - same "cover is the hero"
// principle the old grid card had, just given real visual weight instead
// of a 120px thumbnail. Contributors/tags/favorites are real, server-
// derived data (see server.js's albumContributors()/album.tags/
// favoriteCount) - each is simply omitted when empty/zero, never a
// placeholder. No per-card "Private" badge - every album in this app is
// uniformly private with no per-album visibility field to make that a
// meaningful per-card distinction, so that statement lives once at the
// page level (AlbumsListScreen.tsx's identity strip) instead of repeated
// on every card.
export function AlbumCard({ album, onOptions }: { album: Album; onOptions: (albumId: string) => void }) {
  const navigate = useNavigate();
  const commentCount = album.commentCount ?? album.comments?.length ?? 0;
  const favoriteCount = album.favoriteCount || 0;
  const contributors = album.contributors || [];
  const tags = album.tags || [];

  function open(): void {
    navigate(`/albums/${album.id}`);
  }

  return (
    <div
      className="albums-card"
      role="button"
      tabIndex={0}
      aria-label={`Open ${album.name}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") open();
      }}
    >
      <div className="albums-card-cover">
        <CardCover cover={album.cover} />
        <div className="albums-card-cover-fade" aria-hidden="true" />
        <button
          type="button"
          className="albums-card-menu-btn"
          aria-label={`${album.name} options`}
          onClick={(e) => {
            e.stopPropagation();
            onOptions(album.id);
          }}
        >
          <i className="bi bi-three-dots-vertical" aria-hidden="true" />
        </button>
      </div>
      <div className="albums-card-body">
        <h3 className="albums-card-title">{album.name}</h3>
        <div className="albums-card-counts">
          <span>
            <i className="bi bi-images" aria-hidden="true" /> {album.imageCount || 0}
          </span>
          <span>
            <i className="bi bi-camera-video" aria-hidden="true" /> {album.videoCount || 0}
          </span>
          <span>
            <i className="bi bi-chat-left-text" aria-hidden="true" /> {commentCount}
          </span>
        </div>
        <ContributorsRow contributors={contributors} favoriteCount={favoriteCount} />
        <TagChips tags={tags} />
      </div>
    </div>
  );
}
